// Phase 3 (C1a): PDF orchestrator. Walks every rendered A4 page element on the
// preview screen, captures it to PNG via html-to-image, and stitches the result
// into one A4 PDF via jsPDF. Mirrors the proven pattern from
// src/pages/Reports/AllKeyDiagrams.js — same library choices, same font-embed
// pre-fetch, same fallback to html2canvas if html-to-image fails on a page.
//
// In M1 we ship only sections 1–5 (Modules → Compteurs). C1b in M2 extends this
// orchestrator with bookmarks + the embedded diagrams + later sections.
//
// Inputs
//   pageElements    HTMLElement[] — the A4PageFrame DOM nodes, in display order
//   fileName        suggested file name
//   onProgress      optional fn(currentIndex, total, label)

import { toPng, getFontEmbedCSS } from "html-to-image";
import jsPDF from "jspdf";

// A few CSS properties are enough for our templates (no react-flow here, so we
// can use a tighter list than AllKeyDiagrams.js needs).
const TEMPLATE_STYLE_PROPS = [
  "color", "background", "background-color", "background-image",
  "font", "font-family", "font-size", "font-weight", "font-style",
  "text-align", "text-anchor", "letter-spacing", "line-height",
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
  "border", "border-top", "border-right", "border-bottom", "border-left",
  "border-color", "border-style", "border-width", "border-collapse", "table-layout",
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  "display", "flex", "flex-direction", "align-items", "justify-content", "gap", "flex-shrink", "flex-grow",
  "position", "top", "right", "bottom", "left",
  "fill", "stroke", "stroke-width", "stroke-dasharray", "opacity",
  // SVG schematic (EnergyFlowSchema): rotated <text> uses the SVG transform
  // ATTRIBUTE, but include the CSS props as insurance; baseline/line-cap/opacity
  // round out faithful SVG capture.
  "transform", "transform-origin", "text-anchor", "dominant-baseline",
  "stroke-linecap", "stroke-linejoin", "fill-opacity", "stroke-opacity",
  "white-space", "box-sizing",
  // overflow/text-overflow so fixed-layout tables' ellipsized cells (tdEllipsis)
  // survive the html-to-image clone (Chromium copies ONLY these whitelisted props);
  // without them the clone reverts to visible overflow and columns clip on capture.
  "overflow", "overflow-x", "overflow-y", "text-overflow",
];

export async function exportRepartitionPdf({
  pageElements,
  fileName = "repartition.pdf",
  onProgress = () => { },
  save = true,
}) {
  if (!Array.isArray(pageElements) || pageElements.length === 0) {
    throw new Error("No pages to export");
  }

  // Pre-fetch font CSS once (avoids the ~2s/page penalty html-to-image otherwise
  // pays re-fetching fonts on every call).
  let fontEmbedCSS = "";
  try {
    fontEmbedCSS = await getFontEmbedCSS(pageElements[0]);
  } catch {
    fontEmbedCSS = "";
  }

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const a4WidthPt = pdf.internal.pageSize.getWidth();
  const a4HeightPt = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < pageElements.length; i++) {
    const el = pageElements[i];
    onProgress(i, pageElements.length, el.getAttribute("data-rr-section") || "");

    const rect = el.getBoundingClientRect();
    let pngData = null;
    try {
      pngData = await toPng(el, {
        cacheBust: false,
        pixelRatio: 3,
        fontEmbedCSS,
        includeStyleProperties: TEMPLATE_STYLE_PROPS,
        width: rect.width,
        height: rect.height,
        backgroundColor: "#ffffff",
      });
    } catch (e) {
      // Hard fall back to a second toPng without the style list — slower but
      // less likely to drop content.
      try {
        pngData = await toPng(el, {
          cacheBust: false,
          pixelRatio: 3,
          fontEmbedCSS,
          width: rect.width,
          height: rect.height,
          backgroundColor: "#ffffff",
        });
      } catch (e2) {
        console.error("exportRepartitionPdf: capture failed for page", i, e2);
        continue;
      }
    }

    if (i > 0) pdf.addPage("a4", isLandscape(el) ? "landscape" : "portrait");
    // Fit the captured image into the page area, preserving aspect ratio.
    const targetW = isLandscape(el) ? a4HeightPt : a4WidthPt;
    const targetH = isLandscape(el) ? a4WidthPt : a4HeightPt;
    pdf.addImage(pngData, "PNG", 0, 0, targetW, targetH, undefined, "FAST");
  }

  onProgress(pageElements.length, pageElements.length, "done");
  if (save) {
    pdf.save(fileName);
    return undefined;
  }
  // Caller wants to append more pages (e.g. the key diagrams) before saving.
  return { pdf, a4WidthPt, a4HeightPt, pageCount: pageElements.length };
}

function isLandscape(el) {
  const r = el.getBoundingClientRect();
  return r.width > r.height;
}

// Append extra A4 page elements to an EXISTING jsPDF doc (same capture pipeline
// as exportRepartitionPdf). Used to add the standalone "Schéma de principe des
// flux énergétiques" page as the FINAL page — after the section pages and the
// key diagrams — so the PDF page order matches the client reference.
export async function appendElementsToPdf({
  pdf,
  elements,
  startPdfPageCount = 0,
  fontEmbedCSS = "",
  onProgress = () => { },
}) {
  if (!pdf || !Array.isArray(elements) || elements.length === 0) return 0;
  const a4WidthPt = pdf.internal.pageSize.getWidth();
  const a4HeightPt = pdf.internal.pageSize.getHeight();
  let added = 0;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const rect = el.getBoundingClientRect();
    let pngData = null;
    try {
      pngData = await toPng(el, {
        cacheBust: false,
        pixelRatio: 3,
        fontEmbedCSS,
        includeStyleProperties: TEMPLATE_STYLE_PROPS,
        width: rect.width,
        height: rect.height,
        backgroundColor: "#ffffff",
      });
    } catch (e) {
      try {
        pngData = await toPng(el, {
          cacheBust: false,
          pixelRatio: 3,
          fontEmbedCSS,
          width: rect.width,
          height: rect.height,
          backgroundColor: "#ffffff",
        });
      } catch (e2) {
        console.error("appendElementsToPdf: capture failed for element", i, e2);
        continue;
      }
    }

    // Always add a new page: by the time this runs the doc already has the
    // section + diagram pages, so every appended element starts a fresh page.
    pdf.addPage("a4", isLandscape(el) ? "landscape" : "portrait");
    const targetW = isLandscape(el) ? a4HeightPt : a4WidthPt;
    const targetH = isLandscape(el) ? a4WidthPt : a4HeightPt;
    pdf.addImage(pngData, "PNG", 0, 0, targetW, targetH, undefined, "FAST");
    added += 1;
    onProgress(startPdfPageCount + added);
  }
  return added;
}

